import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import {
  allStepsValid,
  defaultWizardData,
  stepForKey,
  stepValid,
  visibleSteps
} from '@/config/membershipTierWizardSteps'
import { editStepValid } from '@/helpers/membershipTierWizardEditing'
import { buildSetupPayload, draftFromState } from '@/helpers/membershipTierWizardPayload'
import { clearDraft, mergeDraft, readDraft, writeDraft } from '@/helpers/wizardDraft'
import api from '@/store/api'

const GENERIC_ERROR = 'Something went wrong saving this membership. Nothing was changed.'
const LOAD_ERROR = 'This membership could not be loaded.'
const FIRST_STEP = 'name'
const FALLBACK_STEP = 'includes'

const serverErrors = error => {
  const messages = error?.response?.data?.errors
  return Array.isArray(messages) && messages.length ? messages : [GENERIC_ERROR]
}

export function useMembershipTierWizard({ storeId = null, tierId = null, context = null } = {}) {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const editing = Boolean(tierId)
  const stepParam = editing ? 'tab' : 'step'
  const draftKey = `membershipTierWizard:${store.getters.loggedInUserId}`

  const draft = editing ? null : readDraft(draftKey)
  const data = reactive(mergeDraft(defaultWizardData(), draft?.data))
  if (storeId && !draft && !data.joining.storeIds.includes(String(storeId))) data.joining.storeIds.push(String(storeId))

  const stepContext = computed(() => ({ ...(context?.value ?? {}), editing }))
  const steps = computed(() => visibleSteps(data, stepContext.value))
  const questionCount = computed(() => steps.value.filter(s => s.key !== 'review').length)
  const isShown = key => steps.value.some(s => s.key === key)
  const canReview = computed(() => allStepsValid(data, stepContext.value))

  const firstUnfinishedStep = () => steps.value.find(s => s.key !== 'review' && !stepValid(s.key, data))?.key

  const openingStep = requested => {
    if (requested !== 'review') return requested
    return firstUnfinishedStep() ?? 'review'
  }

  const requested = stepForKey(route.params[stepParam])?.key ?? stepForKey(draft?.stepKey)?.key ?? FIRST_STEP
  const stepKey = ref(editing ? requested : openingStep(requested))
  const step = computed(() => steps.value.find(s => s.key === stepKey.value) ?? steps.value[0])
  const valid = computed(() => stepValid(stepKey.value, data))
  const saving = ref(false)
  const errors = ref([])
  const loading = ref(editing)
  const loadError = ref('')
  const meta = ref({})
  const baseline = ref('')

  const payloadJson = () => JSON.stringify(buildSetupPayload(data, { editing }))
  const dirty = computed(
    () => editing && !loading.value && !loadError.value && Boolean(baseline.value) && payloadJson() !== baseline.value
  )
  const baselinePayload = computed(() => (baseline.value ? JSON.parse(baseline.value) : null))
  const stepOk = key =>
    editing
      ? editStepValid(key, data, buildSetupPayload(data, { editing }), baselinePayload.value)
      : stepValid(key, data)
  const blockingStep = computed(() => (dirty.value ? steps.value.find(s => !stepOk(s.key)) ?? null : null))
  const canSubmit = computed(() => (editing ? dirty.value && !blockingStep.value : canReview.value))

  const pristine = () => stepKey.value === FIRST_STEP && JSON.stringify(data) === JSON.stringify(defaultWizardData())

  const persist = () => {
    if (editing) return
    if (pristine()) clearDraft(draftKey)
    else writeDraft(draftKey, { data: JSON.parse(JSON.stringify(data)), stepKey: stepKey.value })
  }
  watch(data, persist, { deep: true })

  const showStep = key => {
    stepKey.value = key
    errors.value = []
    router.replace({ params: { ...(editing ? route.params : {}), [stepParam]: key }, query: route.query })
    persist()
  }

  const go = key => {
    if (!stepForKey(key) || !isShown(key) || key === stepKey.value) return
    if (key === 'review' && !canReview.value) return
    showStep(key)
  }

  const next = () => {
    if (!valid.value) return
    const following = steps.value[step.value.index + 1]
    if (following) go(openingStep(following.key))
  }

  const back = () => {
    const previous = steps.value[step.value.index - 1]
    if (previous) go(previous.key)
  }

  watch(
    () => isShown(stepKey.value),
    shown => {
      if (!shown && !loading.value) showStep(isShown(FALLBACK_STEP) ? FALLBACK_STEP : FIRST_STEP)
    },
    { immediate: true }
  )

  const reset = () => {
    Object.assign(data, defaultWizardData())
    stepKey.value = FIRST_STEP
    errors.value = []
    clearDraft(draftKey)
    router.replace({ params: { [stepParam]: FIRST_STEP }, query: route.query })
  }

  const load = async () => {
    loading.value = true
    loadError.value = ''
    try {
      const response = await api.call('GET', `membership_tiers/${tierId}/setup`, {}, { receive: false, save: false })
      Object.assign(data, draftFromState(response.data.data, response.data.meta))
      meta.value = response.data.meta ?? {}
      baseline.value = payloadJson()
      loadError.value = ''
      if (!isShown(stepKey.value)) stepKey.value = FIRST_STEP
    } catch {
      loadError.value = LOAD_ERROR
    } finally {
      loading.value = false
    }
  }

  const create = async () => {
    const response = await api.call('POST', 'membership_tiers/setup', {
      membership_tier_setup: buildSetupPayload(data)
    })
    clearDraft(draftKey)
    return response.data.data.id
  }

  const update = async () => {
    const response = await api.call('PUT', `membership_tiers/${tierId}/setup`, {
      membership_tier_setup: buildSetupPayload(data, { editing: true })
    })
    await load()
    return response.data.data.id
  }

  const submit = async () => {
    saving.value = true
    errors.value = []
    try {
      return editing ? await update() : await create()
    } catch (error) {
      errors.value = serverErrors(error)
      return null
    } finally {
      saving.value = false
    }
  }

  if (editing) load()

  return {
    data,
    editing,
    steps,
    questionCount,
    step,
    stepKey,
    valid,
    canReview,
    canSubmit,
    blockingStep,
    stepOk,
    dirty,
    loading,
    loadError,
    reload: load,
    meta,
    next,
    back,
    go,
    reset,
    submit,
    saving,
    errors
  }
}
